<template>
    <LoadingComponent :props="loading" />
    <div class="px-5">
        <div class="w-full max-w-md mx-auto rounded-2xl p-8 bg-white border shadow-card mt-24 mb-24 text-center">
            <div class="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-5 text-2xl">
                <i class="fa-solid fa-shield-halved"></i>
            </div>

            <h3 class="text-2xl font-bold text-primary mb-2">{{ $t('label.two_factor_verification') }}</h3>
            <p class="text-sm text-text leading-relaxed mb-6">
                {{ $t('message.enter_digit_code_sent_to', { count: digitCount, recipient: targetRecipient }) }}
            </p>

            <div v-if="errors.validation || errors.code" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mb-6 rounded-lg text-sm relative" role="alert">
                <span>{{ errors.validation || (errors.code ? errors.code[0] : '') }}</span>
            </div>

            <form @submit.prevent="verifyCode">
                <!-- Casillas de dígitos individuales (Experiencia Pro) -->
                <div class="flex items-center justify-center gap-2.5 sm:gap-3 mb-8">
                    <input
                        v-for="(digit, index) in digits"
                        :key="index"
                        :ref="'digitInput_' + index"
                        v-model="digits[index]"
                        type="text"
                        inputmode="numeric"
                        maxlength="1"
                        class="w-12 h-14 sm:w-14 sm:h-16 text-center text-xl font-bold rounded-xl border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all shadow-sm bg-gray-50/50 focus:bg-white"
                        @input="handleInput(index, $event)"
                        @keydown="handleKeyDown(index, $event)"
                        @paste="handlePaste($event)"
                        @focus="$event.target.select()"
                    />
                </div>

                <button
                    type="submit"
                    :disabled="!isCodeComplete || loading.isActive"
                    class="w-full font-bold text-center h-12 leading-12 rounded-full bg-primary text-white capitalize shadow-md hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-6"
                >
                    {{ $t('button.verify_and_sign_in') }}
                </button>
            </form>

            <div class="text-sm text-gray-500">
                <span>{{ $t('message.didnt_receive_code') }} </span>
                <button
                    v-if="resendTimer === 0"
                    type="button"
                    @click="resendCode"
                    class="font-semibold text-primary hover:underline"
                >
                    {{ $t('button.resend_code') }}
                </button>
                <span v-else class="font-medium text-gray-400">
                    {{ $t('message.resend_in', { seconds: resendTimer }) }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import router from "../../../router";
import LoadingComponent from "../components/LoadingComponent";
import alertService from "../../../services/alertService";
import appService from "../../../services/appService";

export default {
    name: "TwoFactorVerifyComponent",
    components: { LoadingComponent },
    props: {
        tempToken: { type: String, default: "" },
        otpDigitLimit: { type: [Number, String], default: 4 },
        email: { type: String, default: "" },
        phone: { type: String, default: "" }
    },
    data() {
        const count = parseInt(this.otpDigitLimit || this.$route.query.digit_limit || 4);
        return {
            loading: { isActive: false },
            digitCount: count,
            digits: Array(count).fill(""),
            token: this.tempToken || this.$route.query.temp_token || "",
            targetRecipient: this.email || this.phone || this.$route.query.recipient || "tu correo/teléfono",
            resendTimer: 60,
            timerInterval: null,
            errors: {},
        };
    },
    computed: {
        isCodeComplete() {
            return this.digits.every(d => d !== "" && d !== null && d !== undefined);
        }
    },
    mounted() {
        this.startResendTimer();
        this.$nextTick(() => {
            this.focusInput(0);
        });
    },
    beforeUnmount() {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
    },
    methods: {
        focusInput(index) {
            const refName = "digitInput_" + index;
            const el = this.$refs[refName];
            if (el && el[0]) {
                el[0].focus();
            }
        },
        handleInput(index, event) {
            const val = event.target.value.replace(/[^0-9]/g, "");
            this.digits[index] = val ? val.slice(-1) : "";

            if (this.digits[index] && index < this.digitCount - 1) {
                this.focusInput(index + 1);
            }

            if (this.isCodeComplete) {
                this.verifyCode();
            }
        },
        handleKeyDown(index, event) {
            if (event.key === "Backspace") {
                if (!this.digits[index] && index > 0) {
                    this.focusInput(index - 1);
                } else {
                    this.digits[index] = "";
                }
            } else if (event.key === "ArrowLeft" && index > 0) {
                this.focusInput(index - 1);
            } else if (event.key === "ArrowRight" && index < this.digitCount - 1) {
                this.focusInput(index + 1);
            }
        },
        handlePaste(event) {
            event.preventDefault();
            const pasteData = (event.clipboardData || window.clipboardData).getData("text");
            const cleanDigits = pasteData.replace(/[^0-9]/g, "").split("").slice(0, this.digitCount);

            if (cleanDigits.length > 0) {
                cleanDigits.forEach((digit, i) => {
                    this.digits[i] = digit;
                });
                const nextFocus = Math.min(cleanDigits.length, this.digitCount - 1);
                this.focusInput(nextFocus);

                if (this.isCodeComplete) {
                    this.verifyCode();
                }
            }
        },
        startResendTimer() {
            this.resendTimer = 60;
            if (this.timerInterval) clearInterval(this.timerInterval);
            this.timerInterval = setInterval(() => {
                if (this.resendTimer > 0) {
                    this.resendTimer--;
                } else {
                    clearInterval(this.timerInterval);
                }
            }, 1000);
        },
        verifyCode() {
            if (!this.isCodeComplete) return;

            const code = this.digits.join("");
            this.loading.isActive = true;
            this.errors = {};

            axios.post("auth/two-factor/verify", {
                temp_token: this.token,
                code: code
            }).then(res => {
                this.loading.isActive = false;
                alertService.success(res.data.message || "Autenticación exitosa");
                this.$store.dispatch("login", {
                    token: res.data.token,
                    user: res.data.user,
                    menu: res.data.menu,
                    permission: res.data.permission,
                    defaultPermission: res.data.defaultPermission
                });

                const redirect = this.$route?.query?.redirect;
                if (redirect && redirect !== "/login") {
                    router.push(redirect);
                } else {
                    router.push({ name: "frontend.account.overview" });
                }
            }).catch(err => {
                this.loading.isActive = false;
                if (err.response && err.response.data && err.response.data.errors) {
                    this.errors = err.response.data.errors;
                } else {
                    alertService.error("Ocurrió un error al verificar el código.");
                }
            });
        },
        resendCode() {
            this.loading.isActive = true;
            axios.post("auth/two-factor/resend", {
                temp_token: this.token
            }).then(res => {
                this.loading.isActive = false;
                alertService.success(res.data.message || "Código reenviado con éxito");
                this.startResendTimer();
            }).catch(err => {
                this.loading.isActive = false;
                alertService.error(err.response?.data?.message || "Error al reenviar el código.");
            });
        }
    }
};
</script>
